import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getJson } from '../api';

const AuthContext = createContext({
  user: null,
  isLoading: true,
  refreshUser: () => Promise.resolve(),
  setUser: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const requestIdRef = useRef(0);

  const setUserSafely = useCallback(
    (value) => {
      requestIdRef.current += 1;
      setUser(value);
      setIsLoading(false);
    },
    [setIsLoading],
  );

  const refreshUser = useCallback(async () => {
    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;
    try {
      const result = await getJson('/auth/session');
      if (requestIdRef.current !== currentRequestId) {
        return;
      }
      setUser(result.user || null);
    } catch (error) {
      if (requestIdRef.current !== currentRequestId) {
        return;
      }
      setUser(null);
    } finally {
      if (requestIdRef.current === currentRequestId) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const value = useMemo(
    () => ({
      user,
      setUser: setUserSafely,
      refreshUser,
      isLoading,
    }),
    [user, isLoading, refreshUser, setUserSafely],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
